import { useCallback } from 'react'
import { type NavigateOptions, useLocation, useNavigate } from 'react-router'
import { useIdCompany } from '@/lib/hooks/use-company-id'

type BasePath =
  | '/'
  | '/my-account'
  | '/my-account/update-name'
  | '/my-account/update-email'
  | '/my-account/update-password'
  | '/my-account/enable-authenticator-app'
  | '/my-account/disable-authenticator-app'
  | '/my-account/revoke-git-login/:gitType'
  | '/my-account/revoke-git-repository-access/:gitType'
  | '/apps'
  | '/apps/create'
  | '/apps/create-pipeline'
  | '/databases'
  | '/databases/create'
  | '/static-sites'
  | '/static-sites/create'
  | '/object-storage'
  | '/load-balancers'
  | '/load-balancers/create'
  | '/templates'
  | '/templates/:name/deploy'
  | '/billing'
  | '/billing/payment-methods'
  | '/usage'
  | '/billing/billing-details'
  | '/billing/invoices'
  | '/billing/invoices/:idInvoice'
  | '/billing/payment-methods/add'
  | '/billing/payment-methods/delete/:idPaymentMethod'
  | '/billing/payment-methods/set-default/:idPaymentMethod'
  | '/billing/delete-company'
  | '/api-keys'
  | '/api-keys/create'
  | '/api-keys/delete/:idApiKey'
  | '/api-keys/update/:idApiKey'
  | '/api-keys/rotate/:idApiKey'
  | '/api-keys/toggle/:idApiKey'
  | '/webhooks'
  | '/webhooks/create'
  | '/webhook/:idWebhook'
  | '/webhook/:idWebhook/overview'
  | '/webhook/:idWebhook/overview/update'
  | '/webhook/:idWebhook/overview/delete'
  | '/webhook/:idWebhook/overview/toggle'
  | '/webhook/:idWebhook/overview/roll-secret'
  | '/webhook/:idWebhook/event-deliveries'
  | '/webhook/:idWebhook/event-deliveries/:idEventDelivery'
  | '/registry-credentials'
  | '/registry-credentials/delete/:idRegistryCredential'
  | '/registry-credentials/create'
  | '/user-management'
  | '/activity-log'
  | '/global-variables'
  | '/global-variables/apps/add'
  | '/global-variables/apps/:idEnvironmentVariable/update'
  | '/single-sign-on'
  | '/single-sign-on/configure/:idSamlSetting'
  | '/single-sign-on/disable'
  | '/single-sign-on/toggle-force-sso'
  | '/single-sign-on/add-exception'
  | '/single-sign-on/remove-exception/:idUser'
  | '/single-sign-on/toggle-jit'
  | '/single-sign-on/change-default-role'
  | '/help/ai-support'

type AppSubPages =
  | 'overview'
  | 'overview/add-internal-connection'
  | 'overview/update-caching'
  | 'overview/create-process'
  | 'overview/update-process/:idProcess'
  | 'overview/manual-deploy'
  | 'overview/update-build-strategy'
  | 'deployments'
  | 'deployments/update-build-strategy'
  | 'deployments/manual-deploy'
  | 'deployment/:idDeployment'
  | 'deployment/:idDeployment/update-build-strategy'
  | 'logs'
  | 'analytics'
  | 'environment-variables'
  | 'environment-variables/:idEnvironmentVariable/update'
  | 'environment-variables/add'
  | 'processes'
  | 'processes/create'
  | 'process/:idProcess/update'
  | 'process/:idProcess/delete'
  | 'process/:idProcess/create-disk'
  | 'process/:idProcess/update-disk'
  | 'process/:idProcess/delete-disk'
  | 'domains'
  | 'domains/add'
  | 'domains/:idDomain/update'
  | 'domains/:idDomain/delete'
  | 'domains/:idDomain/toggle'
  | 'domains/:idDomain/debug'
  | 'domains/:idDomain/dns-records'
  | 'domains/:idDomain/set-primary'
  | 'networking'
  | 'networking/toggle-ingress'
  | 'networking/update-protocol'
  | 'networking/update-port'
  | 'networking/update-caching'
  | 'networking/update-rate-limiting'
  | 'networking/update-ip-restrictions'
  | 'networking/create-tcp-proxy'
  | 'networking/:idTcpProxy/delete-tcp-proxy'
  | 'networking/expose-port'
  | 'networking/add-internal-connection'
  | 'networking/delete-internal-connection/:idInternalConnection'
  | 'networking/:idPort/delete-port'
  | 'networking/update-ingress-port'
  | 'disks'
  | 'disks/create'
  | 'disks/:idProcess/update'
  | 'disks/:idProcess/delete'
  | 'web-terminal'
  | 'settings'
  | 'settings/update-display-name'
  | 'settings/update-hibernation'
  | 'settings/update-maintenance-mode'
  | 'settings/update-source'
  | 'settings/update-auto-deploy'
  | 'settings/toggle-git-lfs'
  | 'settings/toggle-deploy-hook'
  | 'settings/regenerate-deploy-hook'
  | 'settings/update-deploy-paths'
  | 'settings/update-build-strategy'
  | 'settings/delete-app'
  | 'settings/suspend-app'
  | 'settings/activate-app'

type AppRoutes = '/app/:appName' | `/app/:appName/${AppSubPages}`

type PipelinesRoutes =
  | '/pipeline/:idPipeline'
  | '/pipeline/:idPipeline/update'
  | '/pipeline/:idPipeline/update-preview'
  | '/pipeline/:idPipeline/enable-preview'
  | '/pipeline/:idPipeline/disable-preview'
  | '/pipeline/:idPipeline/deploy'
  | '/pipeline/:idPipeline/delete'
  | '/pipeline/:idPipeline/stage/create'
  | '/pipeline/:idPipeline/stage/:idStage/delete'
  | '/pipeline/:idPipeline/stage/:idStage/add-app'
  | '/pipeline/:idPipeline/stage/:idStage/remove-app/:idApp'

type DatabaseRoutes =
  | '/database/:databaseName'
  | '/database/:databaseName/overview'
  | '/database/:databaseName/overview/delete-internal-connection/:idInternalConnection'
  | '/database/:databaseName/overview/add-internal-connection'
  | '/database/:databaseName/overview/change-password'
  | '/database/:databaseName/studio'
  | '/database/:databaseName/networking'
  | '/database/:databaseName/networking/add-internal-connection'
  | '/database/:databaseName/networking/delete-internal-connection/:idInternalConnection'
  | '/database/:databaseName/networking/update-ip-restrictions'
  | '/database/:databaseName/networking/toggle-external-connection'
  | '/database/:databaseName/analytics'
  | '/database/:databaseName/backups'
  | '/database/:databaseName/backups/create'
  | '/database/:databaseName/backups/restore/:backupName'
  | '/database/:databaseName/backups/delete/:backupName'
  | '/database/:databaseName/backups/external'
  | '/database/:databaseName/backups/external/targets/create'
  | '/database/:databaseName/backups/external/targets/update/:idBackupTarget'
  | '/database/:databaseName/backups/external/targets/delete/:idBackupTarget'
  | '/database/:databaseName/backups/external/targets/:idBackupTarget/create-backup'
  | '/database/:databaseName/backups/external/targets/:idBackupTarget/restore/:idBackup'
  | '/database/:databaseName/backups/external/targets/:idBackupTarget/delete/:idBackup'
  | '/database/:databaseName/backups/history'
  | '/database/:databaseName/settings'
  | '/database/:databaseName/settings/update-details'
  | '/database/:databaseName/settings/suspend-database'
  | '/database/:databaseName/settings/activate-database'
  | '/database/:databaseName/settings/restart-database'
  | '/database/:databaseName/settings/delete-database'
type StaticSiteRoutes =
  | '/static-site/:staticSiteName'
  | '/static-site/:staticSiteName/overview'
  | '/static-site/:staticSiteName/overview/manual-deploy'
  | '/static-site/:staticSiteName/deployments'
  | '/static-site/:staticSiteName/deployments/manual-deploy'
  | '/static-site/:staticSiteName/deployments/update-build-strategy'
  | '/static-site/:staticSiteName/deployment/:idDeployment'
  | '/static-site/:staticSiteName/analytics'
  | '/static-site/:staticSiteName/logs'
  | '/static-site/:staticSiteName/environment-variables'
  | '/static-site/:staticSiteName/environment-variables/:idEnvironmentVariable/update'
  | '/static-site/:staticSiteName/environment-variables/add'
  | '/static-site/:staticSiteName/domains'
  | '/static-site/:staticSiteName/domains/add'
  | '/static-site/:staticSiteName/domains/:idDomain/update'
  | '/static-site/:staticSiteName/domains/:idDomain/delete'
  | '/static-site/:staticSiteName/domains/:idDomain/toggle'
  | '/static-site/:staticSiteName/domains/:idDomain/debug'
  | '/static-site/:staticSiteName/domains/:idDomain/dns-records'
  | '/static-site/:staticSiteName/domains/:idDomain/set-primary'
  | '/static-site/:staticSiteName/networking'
  | '/static-site/:staticSiteName/networking/update-rate-limiting'
  | '/static-site/:staticSiteName/settings'
  | '/static-site/:staticSiteName/settings/delete-static-site'
  | '/static-site/:staticSiteName/settings/update-display-name'
  | '/static-site/:staticSiteName/settings/update-auto-deploy'
  | '/static-site/:staticSiteName/settings/toggle-git-lfs'
  | '/static-site/:staticSiteName/settings/update-pull-request-previews'
  | '/static-site/:staticSiteName/settings/update-build-strategy'
  | '/static-site/:staticSiteName/settings/update-pretty-urls'
  | '/static-site/:staticSiteName/settings/update-deploy-paths'
  | '/static-site/:staticSiteName/settings/update-source'
type ObjectStorageRoutes =
  | '/object-storage/create'
  | '/object-storage/:idObjectStorage'
  | '/object-storage/:idObjectStorage/objects'
  | '/object-storage/:idObjectStorage/settings'
  | '/object-storage/:idObjectStorage/settings/delete'
  | '/object-storage/:idObjectStorage/settings/delete-cors-policy/:idCorsPolicy'
  | '/object-storage/:idObjectStorage/settings/create-cors-policy'
  | '/object-storage/:idObjectStorage/settings/toggle-public-access'
  | '/object-storage/:idObjectStorage/settings/rotate-credentials'
type LoadBalancerRoutes =
  | '/load-balancer/:loadBalancerName'
  | '/load-balancer/:loadBalancerName/overview'
  | '/load-balancer/:loadBalancerName/overview/update-load-balancer'
  | '/load-balancer/:loadBalancerName/overview/create-destination'
  | '/load-balancer/:loadBalancerName/overview/update-destination/:idDestination'
  | '/load-balancer/:loadBalancerName/overview/toggle-destination/:idDestination'
  | '/load-balancer/:loadBalancerName/overview/delete-destination/:idDestination'
  | '/load-balancer/:loadBalancerName/overview/delete-load-balancer'
  | '/load-balancer/:loadBalancerName/networking'
  | '/load-balancer/:loadBalancerName/networking/update-rate-limiting'
  | '/load-balancer/:loadBalancerName/settings'
  | '/load-balancer/:loadBalancerName/settings/delete-load-balancer'
  | '/load-balancer/:loadBalancerName/domains'
  | '/load-balancer/:loadBalancerName/domains/add'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/update'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/delete'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/toggle'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/debug'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/dns-records'
  | '/load-balancer/:loadBalancerName/domains/:idDomain/set-primary'
type TemplateRoutes = '/template/:name' | '/template/:name/deploy'
type ProjectsRoutes = '/projects' | '/projects/create'
type ProjectRoutes =
  | '/project/:projectName'
  | '/project/:projectName/services'
  | '/project/:projectName/services/add'
  | '/project/:projectName/usage'
  | '/project/:projectName/access-management'
  | '/project/:projectName/access-management/invite-users'
  | '/project/:projectName/access-management/remove-user/:idUser'
  | '/project/:projectName/access-management/update-user-access/:idUser'
  | '/project/:projectName/access-management/cancel-invitation/:idInvitation'
  | '/project/:projectName/settings'
  | '/project/:projectName/settings/update'
  | '/project/:projectName/settings/suspend-services'
  | '/project/:projectName/settings/delete-project'
type UserManagementRoutes =
  | '/user-management/invite-users'
  | '/user-management/cancel-invitation/:idInvitation'
  | '/user-management/remove-user/:idUser'
  | '/user-management/update-user-access/:idUser'
  | '/user-management/transfer-ownership'

export type Pathname =
  | BasePath
  | AppRoutes
  | PipelinesRoutes
  | DatabaseRoutes
  | StaticSiteRoutes
  | ObjectStorageRoutes
  | LoadBalancerRoutes
  | TemplateRoutes
  | ProjectRoutes
  | ProjectsRoutes
  | UserManagementRoutes

type Variables<P = Pathname> = P extends string
  ? P extends `${string}:${infer Param}/${infer Rest}`
    ? { [K in Param]: string } & Variables<`/${Rest}`>
    : P extends `${string}:${infer Param}`
      ? { [K in Param]: string }
      : Record<string, never>
  : never

type Params = {
  pathname: Pathname
  idCompany?: string
  idProject?: string
  variables?: Variables<Pathname>
  options?: NavigateOptions
  preserveQuery?: boolean
}

function interpolatePath(path: string, variables: Variables<Pathname>) {
  if (!Object.keys(variables).length) {
    return path
  }
  return path.replace(/:([a-zA-Z0-9_]+)/g, (_, key) => {
    const vars = variables as Record<string, string>
    return vars?.[key] !== undefined ? encodeURIComponent(vars[key]) : `:${key}`
  })
}

export function useCompanyPath() {
  const { idCompany: currentIdCompany } = useIdCompany()
  const navigate = useNavigate()
  const location = useLocation()

  const getCompanyPath = useCallback(
    ({
      pathname,
      idCompany,
      idProject,
      variables = {},
      preserveQuery,
    }: Params) => {
      const interpolatedPath = interpolatePath(pathname, variables)
      const params = preserveQuery
        ? new URLSearchParams(location.search)
        : new URLSearchParams()

      if (idCompany !== undefined) {
        params.set('idCompany', idCompany)
      } else if (currentIdCompany) {
        params.set('idCompany', currentIdCompany)
      }

      if (idCompany === '') {
        params.delete('idCompany')
      }

      if (idProject !== undefined) {
        params.set('idProject', idProject)
      }

      if (idProject === '') {
        params.delete('idProject')
      }

      return (
        interpolatedPath + (params.toString() ? `?${params.toString()}` : '')
      )
    },
    [currentIdCompany, location.search]
  )

  const navigateToPath = useCallback(
    function navigateWithCompanyId({
      variables = {},
      options = {},
      ...params
    }: Params) {
      navigate(getCompanyPath({ variables, ...params }), options)
    },
    [navigate, getCompanyPath]
  )

  return { navigateToPath, getCompanyPath }
}
